import { I18n } from 'react-redux-i18n';
import React from 'react';
import styled from 'styled-components';
import { SectionInfo } from '../../components/SectionInfo';
import { Badge } from '../../components/Badge';
import { SearchInput } from '../../components/SearchInput';
import { SortableHeaderButton } from '../../components/SortableTable';
import { ColumnDef } from '../../components/ColumnDisplayModal';
import { PosDrawer } from '../CrmSalesTracking/PosDrawer';
import type {
  PosEntry,
  ActionList,
  ActivityEntry
} from '../CrmSalesTracking/types';
import { getColorToState } from 'utils/pos';
import {
  CardHead,
  CardHeadActions,
  CardTitle,
  CardTitleRow,
  ColDisplayBadge,
  ColDisplayTrigger,
  GV_ROW_LIMIT_OPTIONS,
  GroupByBtn,
  NoData,
  PosNameCell,
  PosStatusChip,
  PosSubtitle,
  RowLimitBar,
  RowLimitFooter,
  SortableTh,
  ExportButton,
  Table,
  TableWrap,
  Td,
  TdNum,
  Th,
  Tr,
  TrackingTabCount,
  countRefs
} from './shared';
import { TableSkeleton } from './loading';
import type {
  GlobalViewRow,
  PosExpandState,
  RowLimit,
  TableSort
} from './types';

export const POS_STATUS_COLOR_STATES: Partial<Record<PosStatusChip, string>> = {
  active: 'positive',
  lost: 'negative',
  towin: 'neutral'
};

export const displayPosStatus = (status?: string | null) => {
  if (status === 'prospect') {
    return I18n.t('restaurants&caves.pos.typeOptions.conquer');
  }
  if (status === 'unknown') {
    return I18n.t('restaurants&caves.pos.typeOptions.other');
  }
  if (status === 'lost') {
    return I18n.t('restaurants&caves.pos.typeOptions.lost');
  }
  return I18n.t('restaurants&caves.pos.typeOptions.pos');
};

export const badgeVariantForPosStatus = (status?: string | null) =>
  status === 'active' || status === 'lost' ? status : ('default' as const);

/**
 * Points of Sale table columns — mirrors proto's expanded `GV_POS_COLUMNS`.
 * `myBrands`/`competitors` are expandable (see `posExpand` state) into one
 * Yes/No sub-column per brand, parsed from the real `client_refs`/
 * `rival_refs` comma-separated name strings. `continent`/`country` are
 * fully removed (no backing field, and dropped per the new spec) and
 * Michelin/Wine Spectator are folded into the single `quality` column
 * instead of standalone ones. The "Real time POS" filter is wired to a
 * field the backend does not populate yet (`is_realtime`) and remains a
 * placeholder rather than simulating values.
 */
export const GV_POS_COLUMNS: ColumnDef[] = [
  {
    id: 'name',
    label: I18n.t('v2GlobalView.pos.columns.name'),
    defaultOn: true,
    always: true
  },
  {
    id: 'filteredRefs',
    label: I18n.t('v2GlobalView.pos.columns.filteredRefs'),
    defaultOn: true
  },
  {
    id: 'myBrands',
    label: I18n.t('v2GlobalView.pos.columns.myBrands'),
    defaultOn: true
  },
  {
    id: 'clientRefs',
    label: I18n.t('v2GlobalView.pos.columns.clientRefs'),
    defaultOn: true
  },
  {
    id: 'evolution',
    label: I18n.t('v2GlobalView.pos.columns.evolution'),
    defaultOn: true
  },
  {
    id: 'competitors',
    label: I18n.t('v2GlobalView.pos.columns.competitors'),
    defaultOn: true
  },
  {
    id: 'format',
    label: I18n.t('v2GlobalView.pos.columns.format'),
    defaultOn: true
  },
  {
    id: 'compFormat',
    label: I18n.t('v2GlobalView.pos.columns.compFormat'),
    defaultOn: true
  },
  {
    id: 'city',
    label: I18n.t('v2GlobalView.pos.columns.city'),
    defaultOn: true
  },
  {
    id: 'quality',
    label: I18n.t('v2GlobalView.pos.columns.quality'),
    defaultOn: true
  },
  {
    id: 'contact',
    label: I18n.t('v2GlobalView.pos.columns.contact'),
    defaultOn: false
  },
  {
    id: 'price',
    label: I18n.t('v2GlobalView.pos.columns.price'),
    defaultOn: false
  },
  {
    id: 'lastChange',
    label: I18n.t('v2GlobalView.pos.columns.lastChange'),
    defaultOn: false
  },
  {
    id: 'rotation',
    label: I18n.t('v2GlobalView.pos.columns.rotation'),
    defaultOn: false
  },
  {
    id: 'totalRefs',
    label: I18n.t('v2GlobalView.pos.columns.totalRefs'),
    defaultOn: false
  },
  {
    id: 'market',
    label: I18n.t('v2GlobalView.pos.columns.market'),
    defaultOn: false
  },
  {
    id: 'status',
    label: I18n.t('v2GlobalView.pos.columns.status'),
    defaultOn: false
  },
  {
    id: 'combinedRefs',
    label: I18n.t('v2GlobalView.pos.columns.combinedRefs'),
    defaultOn: false
  },
  {
    id: 'clientList',
    label: I18n.t('v2GlobalView.pos.columns.clientList'),
    defaultOn: false
  },
  {
    id: 'rivalRefs',
    label: I18n.t('v2GlobalView.pos.columns.rivalRefs'),
    defaultOn: false
  },
  {
    id: 'compList',
    label: I18n.t('v2GlobalView.pos.columns.compList'),
    defaultOn: false
  }
];

export const TrackingToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 16px;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--ink);
  cursor: pointer;
  transition:
    border-color 0.15s,
    color 0.15s,
    background 0.15s;

  svg {
    width: 15px;
    height: 15px;
  }

  &:hover {
    border-color: var(--ink);
  }

  &[aria-pressed='true'] {
    background: var(--ink);
    border-color: var(--ink);
    color: var(--white);
  }
`;

export const StatusChips = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px;
  border-radius: 999px;
  background: #ebecee;
`;

export const StatusDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  flex: 0 0 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

export const RealtimeFilter = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 34px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--white);
  color: var(--ink);
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    margin: 0;
    accent-color: #2f8058;
    cursor: pointer;
  }

  &:focus-within {
    outline: 2px solid var(--ink);
    outline-offset: 2px;
  }
`;

export const StatusChip = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 30px;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid transparent;
  background: ${({ $active }) => ($active ? 'var(--white)' : 'transparent')};
  box-shadow: ${({ $active }) =>
    $active ? '0 1px 2px rgba(43, 61, 84, 0.12)' : 'none'};
  color: var(--ink);
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: var(--white);
  }
`;

export const ChipCount = styled.span`
  font-size: 11px;
  opacity: 0.7;
`;

export const TrackingTabs = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const TrackingTabBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 32px;
  padding: 0 14px;
  background: var(--paper, #f9f7f4);
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink-muted);
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    color: var(--ink);
    border-color: var(--line);
  }
`;

export const ThCheckbox = styled.th`
  width: 40px;
  padding: 14px 0 14px 20px;
  vertical-align: middle;
`;

export const TdCheckbox = styled.td`
  width: 40px;
  padding: 14px 0 14px 20px;
  vertical-align: middle;
`;

export const RowCheck = styled.div<{
  $checked?: boolean;
  $indeterminate?: boolean;
}>`
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 1.5px solid
    ${({ $checked, $indeterminate }) =>
      $checked || $indeterminate
        ? 'var(--terracotta, #c84b1f)'
        : 'var(--line-mid, #d4d0ca)'};
  background: ${({ $checked, $indeterminate }) =>
    $checked || $indeterminate ? 'var(--terracotta, #c84b1f)' : '#fff'};
  cursor: pointer;
  position: relative;
  transition:
    border-color 0.15s,
    background 0.15s;
  &:hover {
    border-color: ${({ $checked, $indeterminate }) =>
      $checked || $indeterminate
        ? 'var(--terracotta, #c84b1f)'
        : 'var(--ink-muted)'};
  }
  &::after {
    content: '${({ $checked, $indeterminate }) =>
      $checked ? '✓' : $indeterminate ? '–' : ''}';
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

export const KeyExpandBtn = styled.button<{ $open?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-right: 4px;
  flex-shrink: 0;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: var(--ink-faint, #8a8578);
  cursor: pointer;
  vertical-align: -4px;
  transition:
    background 0.15s,
    color 0.15s;

  &:hover {
    background: var(--paper, #f9f7f4);
    color: var(--ink, #2a2722);
  }

  svg {
    width: 13px;
    height: 13px;
    transition: transform 0.15s;
    transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  }
`;

export const YesBadge = styled.span`
  color: var(--success, #247a52);
  font-weight: 600;
`;

export const NoBadge = styled.span`
  color: var(--ink-faint);
`;

export const ContactIcons = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const ContactIconBtn = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  color: var(--ink-faint, #8a8578);
  cursor: pointer;
  text-decoration: none;

  &:hover {
    background: var(--paper, #f9f7f4);
    color: var(--ink, #2a2722);
  }

  svg {
    width: 13px;
    height: 13px;
  }
`;

const renderContact = (row: any) => (
  <ContactIcons>
    {row.store_phone && (
      <ContactIconBtn
        href={`tel:${row.store_phone}`}
        title={row.store_phone}
        onClick={(e) => e.stopPropagation()}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
      </ContactIconBtn>
    )}
    {row.store_address && (
      <ContactIconBtn
        title={row.store_address}
        onClick={(e) => e.stopPropagation()}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      </ContactIconBtn>
    )}
    {row.store_url && (
      <ContactIconBtn
        href={row.store_url}
        target="_blank"
        rel="noreferrer"
        title={row.store_url}
        onClick={(e) => e.stopPropagation()}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      </ContactIconBtn>
    )}
    {!row.store_phone && !row.store_address && !row.store_url && (
      <NoData>—</NoData>
    )}
  </ContactIcons>
);

const isExpandable = (id: string): id is 'myBrands' | 'competitors' =>
  id === 'myBrands' || id === 'competitors';
const sortKeyFor = (id: string, name: string) =>
  `${id === 'myBrands' ? 'myBrand' : 'competitor'}:${name}`;

const POS_COLUMN_IDS = GV_POS_COLUMNS.map((c) => c.id);
const POS_NUMERIC_COLUMNS = new Set([
  'filteredRefs',
  'myBrands',
  'clientRefs',
  'competitors',
  'format',
  'compFormat',
  'price',
  'rotation',
  'totalRefs',
  'combinedRefs',
  'rivalRefs'
]);
const POS_WRAP_COLUMNS = new Set([
  'filteredRefs',
  'myBrands',
  'clientRefs',
  'competitors',
  'format',
  'compFormat',
  'price',
  'totalRefs',
  'combinedRefs',
  'rivalRefs'
]);

export const ExpandTh = styled.th`
  width: 26px;
  min-width: 26px;
  padding: 0 !important;
  background: var(--paper, #f9f7f4);
  text-align: center;
  border-bottom: 1px solid var(--line-soft, #e8e5df);
  border-left: 1px solid var(--line-soft, #e8e5df);
  border-right: 1px solid var(--line-soft, #e8e5df);
`;
export const ExpandTd = styled.td`
  width: 26px;
  min-width: 26px;
  padding: 0 !important;
  background: var(--paper, #f9f7f4);
  border-left: 1px solid var(--line-soft, #e8e5df);
  border-right: 1px solid var(--line-soft, #e8e5df);
`;
export const ExpandSubTh = styled(Th)`
  max-width: 110px;
  font-size: 10.5px;
  white-space: normal;
  vertical-align: bottom;
  background: rgba(200, 75, 31, 0.035);
`;
export const ExpandSubTd = styled(Td)`
  background: rgba(200, 75, 31, 0.035);
`;

// Sales Tracking is postponed: the toggle is hidden, the rest stays wired.
const SALES_TRACKING_ENABLED = false;

export interface PointsOfSaleTabProps {
  actionLists: ActionList[];
  activity: ActivityEntry[];
  handlePosRowClick: (row: GlobalViewRow) => void;
  isLoading: boolean;
  isLoadingPos: boolean;
  openTrackingDrawer: (view: 'lists' | 'activity') => void;
  visiblePosData: GlobalViewRow[];
  posBrandNames: (value?: string | null) => string[];
  posColVisibility: Record<string, boolean>;
  posCompetitorsExpanded: string[];
  posDrawerOpen: boolean;
  posExpand: PosExpandState;
  posMyBrandsExpanded: string[];
  posRealtimeOnly: boolean;
  posRowLimit: RowLimit;
  posSort: TableSort<GlobalViewRow>;
  posStatusChip: PosStatusChip;
  renderPosQuality: (row: GlobalViewRow) => React.ReactNode;
  search: string;
  selectedPos: PosEntry | null;
  isExporting: boolean;
  onExport: () => void;
  setPosColModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setPosDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setPosExpand: React.Dispatch<React.SetStateAction<PosExpandState>>;
  setPosRealtimeOnly: React.Dispatch<React.SetStateAction<boolean>>;
  setPosRowLimit: React.Dispatch<React.SetStateAction<RowLimit>>;
  setPosStatusChip: React.Dispatch<React.SetStateAction<PosStatusChip>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  setTrackingOn: (on: boolean) => void;
  statusCounts: Record<PosStatusChip, number>;
  toggleTrackingSelectAll: () => void;
  toggleTrackingSelection: (id: string | number, e: React.MouseEvent) => void;
  trackingOn: boolean;
  trackingSelectedIds: Set<string | number>;
}

export const PointsOfSaleTab: React.FC<PointsOfSaleTabProps> = ({
  actionLists,
  activity,
  handlePosRowClick,
  isLoading,
  isLoadingPos,
  openTrackingDrawer,
  visiblePosData,
  posBrandNames,
  posColVisibility,
  posCompetitorsExpanded,
  posDrawerOpen,
  posExpand,
  posMyBrandsExpanded,
  posRealtimeOnly,
  posRowLimit,
  posSort,
  posStatusChip,
  renderPosQuality,
  search,
  selectedPos,
  isExporting,
  onExport,
  setPosColModalOpen,
  setPosDrawerOpen,
  setPosExpand,
  setPosRealtimeOnly,
  setPosRowLimit,
  setPosStatusChip,
  setSearch,
  setTrackingOn,
  statusCounts,
  toggleTrackingSelectAll,
  toggleTrackingSelection,
  trackingOn,
  trackingSelectedIds
}) => (
  <>
    <TableWrap>
      <CardHead>
        <CardTitleRow>
          <CardTitle>
            Points of Sale
            <SectionInfo
              title="Points of Sale"
              body={
                <>
                  <p>
                    Review the points of sale in the selected scope and their
                    distribution status.
                  </p>
                  <p>
                    Use Active, Lost and To win over to focus the table.
                    {SALES_TRACKING_ENABLED &&
                      ' Then use Sales Tracking to act on selected stores.'}
                  </p>
                </>
              }
            />
          </CardTitle>
          <StatusChips>
            {(['all', 'active', 'lost', 'towin', 'other'] as const).map((s) => (
              <StatusChip
                key={s}
                type="button"
                $active={posStatusChip === s}
                aria-pressed={posStatusChip === s}
                onClick={() => setPosStatusChip(s)}
              >
                {s !== 'all' && (
                  <StatusDot
                    $color={getColorToState(POS_STATUS_COLOR_STATES[s] ?? null)}
                    aria-hidden="true"
                  />
                )}
                {I18n.t(
                  `restaurants&caves.pos.typeOptions.${
                    s === 'active' ? 'pos' : s === 'towin' ? 'conquer' : s
                  }`
                )}{' '}
                <ChipCount>{statusCounts[s]}</ChipCount>
              </StatusChip>
            ))}
          </StatusChips>
          <RealtimeFilter>
            <input
              type="checkbox"
              checked={posRealtimeOnly}
              onChange={(event) => setPosRealtimeOnly(event.target.checked)}
              aria-label="Real-time POS"
            />
            <span>Real-time POS</span>
          </RealtimeFilter>
        </CardTitleRow>
        <CardHeadActions>
          <SearchInput
            placeholder="Search by point of sale…"
            value={search}
            onChange={setSearch}
          />
          {SALES_TRACKING_ENABLED && (
            <TrackingToggle
              type="button"
              aria-pressed={trackingOn}
              onClick={() => setTrackingOn(!trackingOn)}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 11 12 14 22 4" />
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              </svg>
              Sales Tracking
            </TrackingToggle>
          )}
          {trackingOn && (
            <TrackingTabs>
              <TrackingTabBtn
                type="button"
                onClick={() => openTrackingDrawer('lists')}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 11l3 3L22 4" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
                Action Lists{' '}
                <TrackingTabCount>{actionLists.length}</TrackingTabCount>
              </TrackingTabBtn>
              <TrackingTabBtn
                type="button"
                onClick={() => openTrackingDrawer('activity')}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                </svg>
                Activity <TrackingTabCount>{activity.length}</TrackingTabCount>
              </TrackingTabBtn>
            </TrackingTabs>
          )}
          <ColDisplayTrigger
            type="button"
            onClick={() => setPosColModalOpen(true)}
          >
            Columns{' '}
            <ColDisplayBadge>
              {Object.values(posColVisibility).filter(Boolean).length}
            </ColDisplayBadge>
          </ColDisplayTrigger>
          <ExportButton onClick={onExport} disabled={isExporting} />
        </CardHeadActions>
      </CardHead>

      {isLoading || isLoadingPos ? (
        <TableSkeleton
          label={I18n.t('v2GlobalView.pos.loading')}
          columns={Math.max(
            Object.values(posColVisibility).filter(Boolean).length,
            6
          )}
        />
      ) : (
        <Table className={trackingOn ? 'gv-tracking-mode' : undefined}>
          <thead>
            <tr>
              {trackingOn && (
                <ThCheckbox>
                  <RowCheck
                    $checked={
                      trackingSelectedIds.size === visiblePosData.length &&
                      visiblePosData.length > 0
                    }
                    $indeterminate={
                      trackingSelectedIds.size > 0 &&
                      trackingSelectedIds.size < visiblePosData.length
                    }
                    onClick={toggleTrackingSelectAll}
                  />
                </ThCheckbox>
              )}
              {POS_COLUMN_IDS.filter((id) => posColVisibility[id]).map((id) => (
                <React.Fragment key={id}>
                  <Th
                    $align={POS_NUMERIC_COLUMNS.has(id) ? 'right' : undefined}
                    $wrap={POS_WRAP_COLUMNS.has(id)}
                    aria-sort={
                      posSort.sort?.key === id ? posSort.sort.direction : 'none'
                    }
                    title={I18n.t(`v2GlobalView.pos.columnTooltips.${id}`)}
                  >
                    <SortableHeaderButton
                      label={I18n.t(`v2GlobalView.pos.columns.${id}`)}
                      active={posSort.sort?.key === id}
                      direction={posSort.sort?.direction}
                      align={POS_NUMERIC_COLUMNS.has(id) ? 'right' : 'left'}
                      onClick={() => posSort.onSort(id)}
                    />
                  </Th>
                  {isExpandable(id) && (
                    <>
                      <ExpandTh>
                        <KeyExpandBtn
                          type="button"
                          $open={posExpand[id]}
                          aria-expanded={posExpand[id]}
                          title={I18n.t(
                            `v2GlobalView.pos.columnTooltips.${
                              posExpand[id] ? 'expandHide' : 'expandShow'
                            }`
                          )}
                          onClick={() =>
                            setPosExpand((e) => ({ ...e, [id]: !e[id] }))
                          }
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            {posExpand[id] ? (
                              <>
                                <polyline points="4 7 9 12 4 17" />
                                <polyline points="20 7 15 12 20 17" />
                              </>
                            ) : (
                              <>
                                <polyline points="9 7 4 12 9 17" />
                                <polyline points="15 7 20 12 15 17" />
                              </>
                            )}
                          </svg>
                        </KeyExpandBtn>
                      </ExpandTh>
                      {(id === 'myBrands'
                        ? posMyBrandsExpanded
                        : posCompetitorsExpanded
                      ).map((name) => (
                        <ExpandSubTh key={`${id}-${name}`}>
                          <SortableHeaderButton
                            label={name}
                            active={posSort.sort?.key === sortKeyFor(id, name)}
                            direction={posSort.sort?.direction}
                            onClick={() => posSort.onSort(sortKeyFor(id, name))}
                          />
                        </ExpandSubTh>
                      ))}
                    </>
                  )}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {visiblePosData.map((row: any, i: number) => {
              const clientCount = countRefs(row.client_refs);
              const compCount = countRefs(row.rival_refs);
              const clientNames = posBrandNames(row.client_refs);
              const compNames = posBrandNames(row.rival_refs);
              const rowId = row.entity_id ?? i;
              const cells: Record<string, React.ReactNode> = {
                name: (
                  <Td>
                    <PosNameCell>
                      <strong>{row.entity_name}</strong>
                    </PosNameCell>
                  </Td>
                ),
                filteredRefs: <TdNum>{row.facing ?? '—'}</TdNum>,
                myBrands: <TdNum>{new Set(clientNames).size}</TdNum>,
                clientRefs: <TdNum>{clientCount}</TdNum>,
                evolution: <Td>—</Td>,
                competitors: <TdNum>{new Set(compNames).size}</TdNum>,
                format: <TdNum>—</TdNum>,
                compFormat: <TdNum>—</TdNum>,
                city: <Td>{row.store_city_name || '—'}</Td>,
                quality: <Td>{renderPosQuality(row)}</Td>,
                contact: <Td>{renderContact(row)}</Td>,
                price: (
                  <TdNum>
                    {row.av_price != null
                      ? `${row.av_price.toFixed(0)} €`
                      : '—'}
                  </TdNum>
                ),
                lastChange: <Td>—</Td>,
                rotation: <TdNum>—</TdNum>,
                totalRefs: (
                  <TdNum>{row.facing ?? clientCount + compCount}</TdNum>
                ),
                market: <Td>{row.market_name || '—'}</Td>,
                status: (
                  <Td>
                    <Badge
                      variant={badgeVariantForPosStatus(row.pos_status)}
                      dot
                    >
                      {displayPosStatus(row.pos_status)}
                    </Badge>
                  </Td>
                ),
                combinedRefs: <TdNum>{clientCount + compCount}</TdNum>,
                clientList: <Td>{row.client_refs || '—'}</Td>,
                rivalRefs: <TdNum>{compCount}</TdNum>,
                compList: <Td>{row.rival_refs || '—'}</Td>
              };
              return (
                <Tr
                  key={rowId}
                  $selected={trackingSelectedIds.has(rowId)}
                  onClick={() => handlePosRowClick(row)}
                >
                  {trackingOn && (
                    <TdCheckbox>
                      <RowCheck
                        $checked={trackingSelectedIds.has(rowId)}
                        onClick={(e) => toggleTrackingSelection(rowId, e)}
                      />
                    </TdCheckbox>
                  )}
                  {POS_COLUMN_IDS.filter((id) => posColVisibility[id]).map(
                    (id) => (
                      <React.Fragment key={id}>
                        {cells[id]}
                        {isExpandable(id) && (
                          <>
                            <ExpandTd />
                            {(id === 'myBrands'
                              ? posMyBrandsExpanded
                              : posCompetitorsExpanded
                            ).map((name) => (
                              <ExpandSubTd key={`${id}-${name}`}>
                                {(id === 'myBrands'
                                  ? clientNames
                                  : compNames
                                ).includes(name) ? (
                                  <YesBadge>Yes</YesBadge>
                                ) : (
                                  <NoBadge>No</NoBadge>
                                )}
                              </ExpandSubTd>
                            ))}
                          </>
                        )}
                      </React.Fragment>
                    )
                  )}
                </Tr>
              );
            })}
          </tbody>
        </Table>
      )}
      <RowLimitFooter>
        <RowLimitBar role="group">
          {GV_ROW_LIMIT_OPTIONS.map((limit) => (
            <GroupByBtn
              key={limit}
              type="button"
              $active={posRowLimit === limit}
              aria-pressed={posRowLimit === limit}
              onClick={() => setPosRowLimit(limit)}
            >
              {I18n.t('v2GlobalView.marketOverview.showRows', {
                count: limit
              })}
            </GroupByBtn>
          ))}
        </RowLimitBar>
      </RowLimitFooter>
    </TableWrap>

    {/* POS Detail Drawer — mirrors proto's canonical #crm-drawer, opened
          on any POS row click (proto's body-level `tr[data-id]` handler). */}
    <PosDrawer
      pos={selectedPos}
      open={posDrawerOpen}
      onClose={() => setPosDrawerOpen(false)}
    />
  </>
);
